import { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { formatMoney } from "../lib/brand";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import { ImageUpload } from "../components/brand/ImageUpload";
import { toast } from "sonner";
import { CheckCircle, Hourglass, Warning, Drop } from "@phosphor-icons/react";

export default function HydroPayment() {
  const { user } = useAuth();
  const { appId } = useParams();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenshot, setScreenshot] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/payment").then(({ data }) => { if (!cancelled) setSettings(data); });
    const loadApp = async () => {
      try {
        if (appId) {
          const { data } = await api.get(`/hydro/applications/${appId}/public`);
          if (!cancelled) setApp(data);
        } else if (user && user.role === "member") {
          const { data } = await api.get("/hydro/my-application");
          if (!cancelled) setApp(data);
        }
      } catch {
        if (!cancelled) setApp(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadApp();
    return () => { cancelled = true; };
  }, [appId, user]);

  const submit = async () => {
    if (!screenshot) { toast.error("Upload your payment screenshot first"); return; }
    if (!app) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/hydro/applications/${app.id}/submit-payment`, { screenshot_url: screenshot });
      setApp(data);
      toast.success("Hydro payment submitted. Awaiting verification.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (loading || !settings) {
    return <div className="min-h-screen bg-[#050505]"><BrandHeader /><div className="px-6 py-16 text-white/60">Loading…</div></div>;
  }

  if (!app) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <BrandHeader />
        <section className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gold-gradient text-black mb-4">
            <Drop size={22} weight="duotone" />
          </div>
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Hydro Membership</p>
          <h1 className="font-display text-4xl text-white mt-2">No application found</h1>
          <p className="text-white/60 mt-3">Apply for Hydro Membership to continue to payment.</p>
          <button onClick={() => nav("/hydro/apply")} className="tsc-btn-gold mt-6" data-testid="hydro-go-apply">Start application</button>
          <div className="mt-4">
            <Link to="/" className="text-white/55 hover:text-white text-[11px] tracking-luxe uppercase">Back to home</Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const renderStatusBanner = () => {
    if (app.status === "active") return (
      <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 p-5 rounded-md flex gap-3 items-start" data-testid="hydro-status-active">
        <CheckCircle size={24} weight="duotone" />
        <div>
          <p className="font-medium">Hydro Membership Active</p>
          <p className="text-sm opacity-90 mt-1">Welcome to Hydro. Your privileges are now live.</p>
        </div>
      </div>
    );
    if (app.status === "pending_verification") return (
      <div className="border border-amber-500/40 bg-amber-500/10 text-amber-200 p-5 rounded-md flex gap-3 items-start" data-testid="hydro-status-pending-verification">
        <Hourglass size={24} weight="duotone" />
        <div>
          <p className="font-medium">Pending Verification</p>
          <p className="text-sm opacity-90 mt-1">Your payment is under review. We&apos;ll be in touch via the email and phone you provided.</p>
        </div>
      </div>
    );
    if (app.status === "rejected") return (
      <div className="border border-red-500/40 bg-red-500/10 text-red-200 p-5 rounded-md flex gap-3 items-start" data-testid="hydro-status-rejected">
        <Warning size={24} weight="duotone" />
        <div>
          <p className="font-medium">Application not approved</p>
          {app.rejection_reason && <p className="text-sm opacity-90 mt-1">{app.rejection_reason}</p>}
          <p className="text-sm opacity-80 mt-2">You can submit a new payment screenshot below.</p>
        </div>
      </div>
    );
    if (app.status === "suspended" || app.status === "expired") return (
      <div className="border border-red-500/40 bg-red-500/10 text-red-200 p-5 rounded-md flex gap-3 items-start" data-testid={`hydro-status-${app.status}`}>
        <Warning size={24} weight="duotone" />
        <div>
          <p className="font-medium">Status: {app.status}</p>
          <p className="text-sm opacity-90 mt-1">Please contact support to reactivate your Hydro Membership.</p>
        </div>
      </div>
    );
    return null;
  };

  const canSubmit = app.status === "pending_payment" || app.status === "rejected";

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <section className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-10 rise-in">
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Hydro Membership</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-white">Activate your Hydro privilege.</h1>
          <p className="text-white/60 mt-3">
            Welcome <span className="text-white">{app.full_name}</span>. Complete your GCash payment and upload the screenshot — our team will verify and activate your Hydro Membership.
          </p>
          <p className="text-white/40 text-xs mt-2">Application ref · <span className="font-mono text-[#D4AF37] tracking-[0.2em]">{app.id.slice(0, 8).toUpperCase()}</span></p>
        </div>

        {renderStatusBanner()}

        {canSubmit && (
          <div className="grid md:grid-cols-2 gap-8 mt-8">
            <div className="tsc-card p-6" data-testid="hydro-gcash-card">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Step 1 · Pay via GCash</p>
              <div className="font-display text-3xl text-white mt-3">{formatMoney(settings.hydro_membership_price || 0)}</div>
              <p className="text-white/55 text-sm">Hydro Membership · annual privilege</p>
              <div className="mt-5 bg-white p-3 rounded-md inline-block">
                {settings.gcash_qr_url ? (
                  <img src={settings.gcash_qr_url} alt="GCash QR" className="w-56 h-56 object-contain" />
                ) : (
                  <div className="w-56 h-56 flex items-center justify-center text-black/50 text-xs text-center px-4">QR not configured. Use the GCash number below.</div>
                )}
              </div>
              <div className="mt-5 space-y-1.5 text-sm">
                {settings.gcash_account_name && <p className="text-white/70"><span className="text-white/40 mr-2">Name</span>{settings.gcash_account_name}</p>}
                {settings.gcash_number && <p className="text-white/70"><span className="text-white/40 mr-2">GCash #</span><span className="font-mono">{settings.gcash_number}</span></p>}
              </div>
              {settings.hydro_payment_instructions && (
                <p className="text-white/55 text-xs mt-5 leading-relaxed whitespace-pre-wrap">{settings.hydro_payment_instructions}</p>
              )}
            </div>

            <div className="tsc-card p-6">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Step 2 · Upload screenshot</p>
              <p className="text-white/60 text-sm mt-2">JPG / PNG / WEBP only. Maximum 5MB.</p>
              <div className="mt-5">
                <ImageUpload value={screenshot} onChange={setScreenshot} aspect="aspect-[3/4]" testId="hydro-payment-screenshot" />
              </div>
              <button onClick={submit} disabled={!screenshot || submitting} className="tsc-btn-gold w-full mt-6" data-testid="hydro-submit-payment-btn">
                {submitting ? "Submitting…" : "Submit Payment for Verification"}
              </button>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
